/**
 * Breadcrumbs — navigational trail.
 *
 * Design system mapping:
 *   • text-sm
 *   • Item link: text-webex-blue-700 dark: webex-blue-400 hover:underline
 *   • Current:   text-webex-gray-700 dark: webex-gray-300 (no link)
 *   • Separator: text-webex-gray-400 dark: webex-gray-500 (default "/")
 *
 * Composition:
 *   <Breadcrumbs.Root>
 *     <Breadcrumbs.Item href="/">Home</Breadcrumbs.Item>
 *     <Breadcrumbs.Separator />
 *     <Breadcrumbs.Item href="/orgs">Organizations</Breadcrumbs.Item>
 *     <Breadcrumbs.Separator />
 *     <Breadcrumbs.Current>Acme Corp</Breadcrumbs.Current>
 *   </Breadcrumbs.Root>
 */
import * as React from "react"
import { cn } from "@lib/cn"

function Root({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <nav aria-label="Breadcrumb" className={cn("text-sm", className)} {...props}>
      <ol className="flex items-center gap-1.5 flex-wrap">{children}</ol>
    </nav>
  )
}

const Item = React.forwardRef<HTMLAnchorElement, React.AnchorHTMLAttributes<HTMLAnchorElement> & { render?: React.ReactElement }>(
  ({ className, children, render, ...props }, ref) => {
    const cls = cn(
      "text-webex-blue-700 dark:text-webex-blue-400 hover:underline rounded",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
      className,
    )
    if (render) {
      return (
        <li className="inline-flex items-center">
          {React.cloneElement(
            render,
            {
              className: cn(cls, (render.props as { className?: string }).className),
              ref,
              ...props,
            } as React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLAnchorElement> },
            children,
          )}
        </li>
      )
    }
    return (
      <li className="inline-flex items-center">
        <a ref={ref} className={cls} {...props}>
          {children}
        </a>
      </li>
    )
  },
)
Item.displayName = "Breadcrumbs.Item"

function Current({ className, children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <li className="inline-flex items-center">
      <span
        aria-current="page"
        className={cn("text-webex-gray-700 dark:text-webex-gray-300", className)}
        {...props}
      >
        {children}
      </span>
    </li>
  )
}

function Separator({
  className,
  children = "/",
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <li
      aria-hidden="true"
      className={cn("text-webex-gray-400 dark:text-webex-gray-500 select-none", className)}
      {...props}
    >
      {children}
    </li>
  )
}

export const Breadcrumbs = { Root, Item, Current, Separator }
